import { useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toUserMessage } from '@platform/api-client';
import { formatLastSeen } from '@platform/billing';
import { DeviceNicknameField } from '@/features/devices';
import { LuMonitor } from '@platform/icon';

import { Alert, RouterLinkButton } from '@/shared/ui';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { useAuth } from '@/features/auth';
import { DESKTOP_QUERY, parseAuthorizeParams } from '../lib/authorize-params';
import { DESKTOP_LOOPBACK_WAIT_MS } from '../lib/complete-result';
import { useDesktopAuthorizePreview } from '../api/use-desktop-authorize.query';
import { useDesktopAuthorizeDecision } from '../api/use-desktop-authorize.mutation';
import type {
  DesktopAuthorizeParams,
  DesktopAuthorizePreview,
} from '../types/desktop-auth.type';

/**
 * 데스크톱(XCOST) 로그인 동의 화면 — `/desktop/authorize`.
 *
 * 로그인돼 있어도 반드시 한 번 누르게 한다. 자동 허용이면 사용자가 모르는 사이에
 * 다른 프로그램이 이 계정으로 로그인할 수 있다.
 * 이동 주소는 서버가 검증·조립한 값만 쓴다(오픈 리다이렉트 방지).
 */
export function DesktopAuthorizeView() {
  const [searchParams] = useSearchParams();
  const params = useMemo(
    () => parseAuthorizeParams(searchParams),
    [searchParams],
  );
  const preview = useDesktopAuthorizePreview(params);

  if (!params) {
    return (
      <Shell title="잘못된 로그인 요청입니다">
        <Alert tone="error">
          로그인 요청 정보가 없거나 올바르지 않습니다. 프로그램에서 로그인을 다시
          시도해 주세요.
        </Alert>
      </Shell>
    );
  }

  if (preview.isLoading) {
    return (
      <Shell title="프로그램 로그인">
        <p className="py-10 text-center text-sm text-adobe-muted">확인 중…</p>
      </Shell>
    );
  }

  if (preview.isError || !preview.data) {
    return (
      <Shell title="로그인할 수 없습니다">
        <Alert tone="error">{toUserMessage(preview.error)}</Alert>
      </Shell>
    );
  }

  return <ConsentCard params={params} preview={preview.data} />;
}

function ConsentCard({
  params,
  preview,
}: {
  params: DesktopAuthorizeParams;
  preview: DesktopAuthorizePreview;
}) {
  const { user } = useAuth();
  const decision = useDesktopAuthorizeDecision(params);
  const leaving = decision.isPending || decision.isSuccess;
  // 같은 PC 에서 다시 로그인하는 경우는 경고하지 않는다(서버가 기기 해시로 판정).
  const otherSession =
    preview.activeSession && !preview.activeSession.isThisDevice
      ? preview.activeSession
      : null;
  const [nickname, setNickname] = useState('');
  // 이 화면을 연 시각. XCOST 리스너는 이보다 먼저 열리므로, 여기서 5분이 지났으면 이미 닫혀 있다.
  const [openedAt] = useState(() => Date.now());
  const [expired, setExpired] = useState(false);
  const revoked = preview.deviceCapacity?.revoked ?? false;
  const blocked = !revoked && (preview.deviceCapacity?.blocked ?? false);
  const devicesHref = preview.deviceLabel
    ? `${PATHS.DESKTOP_DEVICES}?${new URLSearchParams({
        [DESKTOP_QUERY.DEVICE_LABEL]: preview.deviceLabel,
      }).toString()}`
    : PATHS.DESKTOP_DEVICES;

  // 닫힌 리스너로 보내면 브라우저 연결 오류 화면만 남는다 — 이동하지 않고 다시 시작을 안내한다.
  const decide = (choice: 'allow' | 'deny') => {
    if (Date.now() - openedAt > DESKTOP_LOOPBACK_WAIT_MS) {
      setExpired(true);
      return;
    }
    decision.mutate(
      choice === 'allow'
        ? { decision: 'allow', deviceNickname: nickname.trim() || undefined }
        : { decision: 'deny' },
    );
  };

  if (expired) {
    return (
      <Shell title="로그인 요청 시간이 지났습니다">
        <p className="text-sm leading-6 text-adobe-muted">
          프로그램이 더 이상 이 로그인을 기다리지 않습니다. 이 창을 닫고 프로그램에서{' '}
          <b>[다시 로그인]</b>을 눌러 주세요.
        </p>
      </Shell>
    );
  }

  if (decision.isSuccess) {
    return (
      <Shell title={`${preview.clientName}(으)로 돌아갑니다`}>
        <p className="text-sm text-adobe-muted">
          프로그램에서 로그인이 이어집니다. 이 창은 닫아도 됩니다.
        </p>
      </Shell>
    );
  }

  return (
    <Shell title={`${preview.clientName}에 로그인`}>
      <div className="flex items-center gap-3 rounded-xl border border-black/[0.08] p-4 dark:border-white/10">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
          <LuMonitor className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-adobe-dark dark:text-neutral-100">
            {preview.deviceLabel ?? '이 PC'}
          </p>
          <p className="truncate text-xs text-adobe-muted">
            {user?.loginId ?? user?.email} 계정으로 로그인합니다
          </p>
        </div>
      </div>

      {revoked && (
        <div className="mt-4">
          <Alert tone="error">
            관리자가 회수한 PC입니다. 이 계정으로는 이 PC에서 프로그램을 사용할 수
            없습니다. 관리자에게 문의해 주세요.
          </Alert>
        </div>
      )}

      {blocked && (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
          <p className="text-sm font-bold text-amber-900 dark:text-amber-100">
            등록된 PC가 모두 사용 중입니다
          </p>
          <p className="mt-1 text-sm leading-6 text-amber-900 dark:text-amber-100">
            이 PC에서 로그인하려면 사용하지 않는 PC를 하나 해제한 뒤 프로그램에서 다시
            로그인해 주세요.
          </p>
          <RouterLinkButton to={devicesHref} size="sm" className="mt-3">
            등록된 PC 정리하기
          </RouterLinkButton>
        </div>
      )}

      {/* 새 PC 일 때만 묻는다. 자리 확인을 못 했으면(null) 등록 여부를 몰라 묻지 않는다. */}
      {preview.deviceCapacity?.registered === false && !blocked && !revoked && (
        <div className="mt-4">
          <DeviceNicknameField
            value={nickname}
            onChange={setNickname}
            label="이 PC 이름 (선택)"
            hint="등록된 PC를 구분할 때 쓰입니다. 기업 좌석을 배정받았다면 조직 관리자에게도 보입니다."
          />
        </div>
      )}

      {otherSession && !blocked && !revoked && (
        <div className="mt-4">
          <Alert tone="warning">
            <b>{otherSession.deviceLabel ?? '다른 PC'}</b>에서 이미 로그인되어
            있습니다(마지막 활동 {formatLastSeen(otherSession.lastActiveAt)}).
            허용하면 그 PC는 로그아웃됩니다. 한 계정은 한 PC에서만 사용할 수
            있습니다.
          </Alert>
        </div>
      )}

      <p className="mt-4 text-sm leading-6 text-adobe-muted dark:text-neutral-400">
        허용하면 이 PC의 {preview.clientName}이(가) 위 계정으로 로그인됩니다.
        직접 실행한 로그인이 아니라면 <b>거절</b>하세요.
      </p>

      <div className="mt-6 flex gap-2">
        <button
          type="button"
          disabled={leaving}
          onClick={() => decide('deny')}
          className="flex-1 rounded-lg border border-black/[0.12] py-2.5 text-sm font-bold text-adobe-dark transition-colors hover:bg-neutral-50 disabled:opacity-50 dark:border-white/15 dark:text-neutral-100 dark:hover:bg-white/5"
        >
          거절
        </button>
        {/* brand-500 위 흰 글자는 2.9:1 이라 AA 미달 — 700(5.0:1)을 쓴다. */}
        {/* 자리가 없거나 회수된 PC 는 허용해도 서버가 거절한다 — 버튼을 두지 않는다. */}
        {!blocked && !revoked && (
          <button
            type="button"
            disabled={leaving}
            onClick={() => decide('allow')}
            className="flex-1 rounded-lg bg-brand-700 py-2.5 text-sm font-bold text-white transition-colors hover:bg-brand-800 disabled:opacity-50"
          >
            {decision.isPending ? '처리 중…' : '허용'}
          </button>
        )}
      </div>
    </Shell>
  );
}

function Shell({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-md py-10">
      <header className="mb-6">
        <h1 className="text-2xl font-bold -tracking-[0.02em] text-adobe-dark dark:text-neutral-100">
          {title}
        </h1>
      </header>
      {children}
    </div>
  );
}
